import * as logClient from "@classdojo/log-client";
import * as Telemetry from "@web-monorepo/telemetry";
import { useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useWatch } from "@web-monorepo/hooks";
import { ProductAreaContext } from "@web-monorepo/telemetry/productArea";
import { sendLogEvent } from "@web-monorepo/utilities";
import { sanitizeQueryString } from "@web-monorepo/telemetry/sanitizeQueryString";

// Put this inside the top level component within your project's router
// (probably called ApplicationContainer)

export type LoggerContextOptions = {
  entityType?: "teacher" | "parent" | "studentUser" | "preSignUp" | undefined;
  fetchedFeatureSwitches: Record<string, string> | undefined;
  prefix: "teach" | "student" | "home" | "marketplace";
  experimentsObject?: { [k: string]: string[] };
};

export function useLoggerContext({
  entityType,
  fetchedFeatureSwitches,
  prefix,
  experimentsObject,
}: LoggerContextOptions) {
  // Technically we won't actually hear about the product area changes because they're now refs, but...
  // We only change it right now when the route changes, which this component is already re-rendering on anyway.
  // We also forcibly set productArea in the logClient in the productArea setting components,
  // in case there are timing differences.
  const productAreaContext = useContext(ProductAreaContext);

  // route, in the context of our event system includes the whole href:
  const route = getRoute(window.location.href, useParams());
  const location = useLocation();
  const path = sanitizeQueryString(location?.pathname);
  const routeName = getRoute(location.pathname, useParams());

  useWatch([path, route, routeName], ([path, route, routeName], previous) => {
    logClient.setPath(path);
    logClient.setRoute(route);
    if (!previous) return;

    const [fromPath, fromRoute] = previous;
    const experiments = experimentsObject?.[routeName];
    sendLogEvent({
      eventName: `${prefix}.routerNavigation`,
      automatedEvent: true,
      metadata: { path, fromPath, route, fromRoute },
      ...(experiments && { experiments }),
    });
  });

  useWatch(productAreaContext?.current, (productArea) => {
    if (productArea) {
      logClient.setProductArea(productArea);
    }
    Telemetry.setTag("productArea", productArea || undefined);
  });
  useWatch(entityType, (entityType) => entityType && logClient.setEntityType(entityType));
  useWatch(
    fetchedFeatureSwitches,
    (featureSwitches) => featureSwitches && logClient.setFeatureSwitches(featureSwitches),
    { deep: true },
  );
}

export const getRoute = (route: string, params: Record<string, string | undefined>) => {
  Object.keys(params || {}).forEach((key) => {
    const val = params[key];
    if (val) {
      route = route.replace(val, `:${key}`);
    }
  });
  return sanitizeQueryString(route);
};
